import { FC } from 'react';
import clsx from 'clsx';

interface SliderProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  labels?: string[];
  error?: string;
  disabled?: boolean;
  className?: string;
  showValue?: boolean;
}

export const Slider: FC<SliderProps> = ({
  label,
  value,
  onChange,
  min = 0,
  max = 3,
  step = 1,
  labels = ['Mauvais', 'Moyen', 'Bon', 'Excellent'],
  error,
  disabled = false,
  className,
  showValue = true,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(Number(e.target.value));
  };

  // Calculate percentage for gradient background
  const percentage = ((value - min) / (max - min)) * 100;

  // Get label for current value
  const getCurrentLabel = () => {
    if (labels && labels.length > 0) {
      const index = Math.min(Math.floor(value), labels.length - 1);
      return labels[index];
    }
    return value.toString();
  };

  return (
    <div className={clsx('w-full', className)}>
      {/* Header avec label et valeur actuelle */}
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
        {showValue && (
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {getCurrentLabel()}
          </span>
        )}
      </div>

      {/* Slider */}
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          className={clsx(
            'w-full h-2 rounded-lg appearance-none cursor-pointer',
            'focus:outline-none focus:ring-2 focus:ring-blue-500',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            // Track (rail)
            'bg-gray-200 dark:bg-gray-700',
            // Thumb (handle) — 20px for desktop, 28px touch target on mobile
            '[&::-webkit-slider-thumb]:appearance-none',
            '[&::-webkit-slider-thumb]:w-5',
            '[&::-webkit-slider-thumb]:h-5',
            '[&::-webkit-slider-thumb]:rounded-full',
            '[&::-webkit-slider-thumb]:bg-blue-600',
            '[&::-webkit-slider-thumb]:cursor-pointer',
            '[&::-webkit-slider-thumb]:hover:bg-blue-700',
            '[&::-webkit-slider-thumb]:transition-colors',
            '[&::-webkit-slider-thumb]:shadow-md',
            // Firefox
            '[&::-moz-range-thumb]:w-5',
            '[&::-moz-range-thumb]:h-5',
            '[&::-moz-range-thumb]:rounded-full',
            '[&::-moz-range-thumb]:bg-blue-600',
            '[&::-moz-range-thumb]:border-0',
            '[&::-moz-range-thumb]:cursor-pointer',
            '[&::-moz-range-thumb]:hover:bg-blue-700',
            '[&::-moz-range-thumb]:transition-colors',
            '[&::-moz-range-thumb]:shadow-md'
          )}
          style={{
            background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${percentage}%, #e5e7eb ${percentage}%, #e5e7eb 100%)`,
            touchAction: 'pan-y',
          }}
        />
      </div>

      {/* Labels aux extrémités */}
      {labels && labels.length >= 2 && (
        <div className="flex justify-between mt-1">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {labels[0]}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {labels[labels.length - 1]}
          </span>
        </div>
      )}

      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};
